import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Lightning, Target, Brain, Bell, ChartLineUp, Sparkle, ShieldCheck, Trophy,
} from "@phosphor-icons/react";
import Logo from "../Logo";
import TelegramCTA from "../TelegramCTA";
import { api } from "../../lib/api";

const FEATURES = [
  { icon: Lightning, label: "Volume-confirmed breakouts" },
  { icon: Target, label: "Auto Entry / SL / Target" },
  { icon: Brain, label: "AI fundamental verdict" },
  { icon: Bell, label: "Instant SL/Target alerts" },
  { icon: ChartLineUp, label: "Sector top movers" },
];

export default function AuthHero() {
  const [stats, setStats] = useState(null);
  const [sample, setSample] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/stats").then(({ data }) => {
      if (!cancelled) setStats(data);
    }).catch(() => { /* silent — stats panel just hides */ });
    api.get("/public/sample-signal").then(({ data }) => {
      if (!cancelled) setSample(data);
    }).catch(() => { /* silent — falls back to placeholder card */ });
    return () => { cancelled = true; };
  }, []);

  const last30 = stats?.last_30d;
  const showStats = last30 && (last30.wins || 0) >= 3; // hide until 3+ wins
  return (
    <div className="px-6 lg:px-12 py-10 lg:py-14 flex flex-col justify-center">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.7, type: "spring" }}
          className="mb-8 inline-block"
        >
          <div className="relative">
            <div
              aria-hidden
              className="absolute inset-0 rounded-full blur-2xl opacity-40 scale-150"
              style={{ background: "radial-gradient(circle, hsl(160 84% 39% / 0.35), transparent 70%)" }}
            />
            <div className="relative p-1">
              <Logo size={96} />
            </div>
          </div>
        </motion.div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-mod/40 bg-mod/10 text-mod font-mono text-[10px] uppercase tracking-[0.25em]"
            data-testid="ai-tagline-pill"
          >
            <Sparkle size={10} weight="fill" /> AI powered · NSE Breakout Scanner
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-pos-soft bg-pos-soft text-pos font-mono text-[10px] uppercase tracking-[0.25em]">
            <Sparkle size={10} weight="fill" /> Limited time · ₹5 for 5 days
          </div>
        </div>

        <h1 className="font-medium leading-[0.95] tracking-tight text-primary-fg text-[44px] sm:text-[60px] lg:text-[72px]">
          Find <span className="text-pos">high-probability</span> breakout stocks
          <br />
          before they explode<span className="text-pos">.</span>
        </h1>

        <p className="mt-6 max-w-xl text-secondary-fg text-base lg:text-lg leading-relaxed">
          Institutional-grade <span className="text-primary-fg">breakout / multi-year breakout signals</span> across NSE — volume-confirmed
          with precise Entry, Stop-Loss and Target levels for disciplined trading.
        </p>

        <div className="mt-8 flex flex-wrap gap-2">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 + i * 0.05 }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-subtle bg-surface/80 backdrop-blur-sm"
            >
              <f.icon size={12} weight="bold" className="text-pos" />
              <span className="text-[11px] font-mono uppercase tracking-wider text-secondary-fg">{f.label}</span>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.35 }}
          className="mt-10 max-w-md border border-subtle rounded-sm overflow-hidden bg-surface/90 backdrop-blur-sm"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-subtle bg-app/40">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">/// Sample signal · today</div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-pos flex items-center gap-1">
              <Lightning size={10} weight="fill" /> Strong
            </span>
          </div>
          <div className="p-4">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-mono text-xl font-semibold text-primary-fg tracking-wide">
                  {sample?.symbol || "DATAPATTNS"}
                </div>
                <div className="text-xs text-muted-fg truncate max-w-[220px]">
                  {sample?.company_name || "Data Patterns (India) Ltd"}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-xl text-primary-fg tabular-nums">
                  ₹{(sample?.last_price ?? 4135.10).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className={
                  "text-[11px] font-mono " +
                  ((sample?.pct_change_today ?? 3.91) >= 0 ? "text-pos" : "text-neg")
                }>
                  {(sample?.pct_change_today ?? 3.91) >= 0 ? "+" : ""}{(sample?.pct_change_today ?? 3.91).toFixed(2)}% today
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-subtle">
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">Entry</div>
                <div className="font-mono text-sm text-primary-fg">
                  ₹{(sample?.entry ?? 4135).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </div>
              </div>
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">SL</div>
                <div className="font-mono text-sm text-neg">
                  ₹{(sample?.stop_loss ?? 3444).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </div>
              </div>
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">Target</div>
                <div className="font-mono text-sm text-pos">
                  ₹{(sample?.target ?? 5517).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* === Last 30-day track record === */}
        {showStats && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.45 }}
            className="mt-6 max-w-md"
            data-testid="auth-hero-track-record"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg flex items-center gap-1.5">
                <Trophy size={11} weight="fill" className="text-pos" />
                Last 30 days · live track record
              </div>
              <a href="/wins" className="text-[10px] font-mono uppercase tracking-widest text-pos hover:underline">
                View all →
              </a>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="border border-subtle rounded-sm p-2.5 bg-surface/80 backdrop-blur-sm">
                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">Win rate</div>
                <div className="font-mono text-base text-pos tabular-nums">{last30.win_rate}%</div>
                <div className="text-[9px] text-muted-fg">{last30.wins}W · {last30.losses}L</div>
              </div>
              <div className="border border-subtle rounded-sm p-2.5 bg-surface/80 backdrop-blur-sm">
                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">Avg win</div>
                <div className="font-mono text-base text-pos tabular-nums">+{last30.avg_pnl_pct}%</div>
                <div className="text-[9px] text-muted-fg">on target hits</div>
              </div>
              <div className="border border-subtle rounded-sm p-2.5 bg-surface/80 backdrop-blur-sm">
                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">Best win</div>
                <div className="font-mono text-base text-pos tabular-nums">+{last30.best_win_pct}%</div>
                <div className="text-[9px] text-muted-fg">single trade</div>
              </div>
            </div>
            {(last30.top_3_wins || []).length > 0 && (
              <div className="border border-subtle rounded-sm bg-surface/80 backdrop-blur-sm">
                <div className="px-3 py-2 text-[9px] font-mono uppercase tracking-widest text-muted-fg border-b border-subtle">
                  Top 3 winners
                </div>
                <div className="divide-y divide-subtle">
                  {last30.top_3_wins.map((w) => (
                    <div key={w.symbol} className="flex items-center justify-between px-3 py-2">
                      <span className="font-mono text-sm text-primary-fg">{w.symbol}</span>
                      <span className="font-mono text-sm text-pos tabular-nums">+{w.pnl_pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        <div className="mt-10 flex flex-wrap items-center gap-6 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
          <span className="flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-pos" /> Secure · Razorpay
          </span>
          <span>Cancel anytime</span>
        </div>

        {/* SEBI compliance banner — prominent legal disclaimer */}
        <div
          className="mt-4 px-3 py-2.5 border border-mod/40 bg-mod-soft/30 rounded-sm text-[10px] font-mono leading-relaxed text-mod"
          data-testid="auth-sebi-disclaimer"
        >
          <div className="font-bold uppercase tracking-widest mb-1">⚠️ Important disclosure</div>
          <span className="normal-case text-secondary-fg">
            Breakout Scanner is an <b>educational/research tool</b>. We are{" "}
            <b className="text-mod">NOT a SEBI-registered Research Analyst</b> or
            Investment Advisor. Signals shown are technical observations, not
            investment recommendations. Trading involves substantial risk of
            loss. Consult your SEBI-registered advisor before any trade.
          </span>
        </div>

        <div className="mt-6">
          <TelegramCTA variant="banner" testId="auth-hero-telegram" />
        </div>
      </motion.div>
    </div>
  );
}
